import {
  ChecklistTemplateKind,
  useChecklistDoneForTemplate,
  useChecklistTemplates,
  type ChecklistDoneForTemplateDto
} from '@domain/hroc-checklists'
import {SortOrder} from '@domain/hroc-checklists'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {DataContentWrapper} from '@hconnect/common/containers'
import {type TimeZone} from '@hconnect/common/types'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Close as CloseIcon} from '@mui/icons-material'
import {Box, Stack, Typography} from '@mui/material'
import {ChecklistTemplateDetails, CompletedChecklistsTable} from '@widgets/hroc-checklists'

type Props = {
  plantId: string
  templateId: string
  page: number
  rowsPerPage: number
  sortOrder: SortOrder
  onSortChange: (columnKey: keyof ChecklistDoneForTemplateDto, sortOrder: SortOrder) => void
  onPaginationChange: (pageNumber: number, itemsPerPage: number) => void
  onChecklistClick: (checklistId: string) => void
  onClose: () => void
  timezone: TimeZone
}

export const ChecklistTemplateContainer = ({
  plantId,
  templateId,
  onClose,
  page,
  rowsPerPage,
  sortOrder,
  onSortChange,
  onPaginationChange,
  onChecklistClick,
  timezone
}: Props) => {
  const isKilnCheckSheetEnabled = useCommonFeatureFlag(FeatureFlagKey.ChecklistsKilnCheckSheet, {
    plantId
  })

  const templateKinds = isKilnCheckSheetEnabled ? undefined : [ChecklistTemplateKind.TakeFive]

  const {data: template} = useChecklistTemplates(
    {plantId, templateKinds},
    {refetchOnMount: false, select: (data) => data.find((t) => t.id === templateId)}
  )
  const {data, isLoading, isFetching, isRefetching, isError, refetch} = useChecklistDoneForTemplate(
    {plantId, templateId, page, rowsPerPage, sortOrder},
    {enabled: !!template}
  )
  if (!template) return null

  return (
    <ContentBox
      mode="max100PercentOfParentHeight"
      title={template.name}
      bodyWithPadding
      afterTitle={
        <Box display="flex" alignItems="center">
          <Typography
            sx={{
              p: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <CloseIcon
              onClick={onClose}
              color="primary"
              data-test-id="checklist-template-details-card-close"
            />
          </Typography>
        </Box>
      }
      data-test-id="checklist-template-details-card"
    >
      <Stack spacing={3}>
        <ChecklistTemplateDetails type={template.type} description={template.description} />
        <DataContentWrapper
          showLoader={isLoading}
          isError={isError}
          data={data}
          retryFunction={() => void refetch()}
          isRefetching={isRefetching}
          progressSx={{color: 'primary.main'}}
        >
          <CompletedChecklistsTable
            data={data?.items ?? []}
            isLoading={isFetching}
            onRowClick={(checklist) => onChecklistClick(checklist.id)}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
            timezone={timezone}
            paginationOptions={{
              page,
              rowsPerPage,
              totalDataLength: data?.totalCount ?? 0,
              onPaginationChange
            }}
          />
        </DataContentWrapper>
      </Stack>
    </ContentBox>
  )
}
